import _ from 'lodash';
import {executeUntil, nextAnimationFrame, nextTick} from "@/utils/Executors";
import Log from "@/utils/Log";
import {isPromise} from "@/utils/Objects";

let filterSerial = 0;

export class VirtualArray{
    /**
     * @param {DataGrid2} dataGrid
     */
    constructor(dataGrid) {

        ///** @type {Map<string, number>} */
        //this.pkIndex = new Map();
        this.attach(dataGrid);
        this.filterIsAsync = false;
    }

    /**
     * @param {DataGrid2} dataGrid
     */
    attach(dataGrid){

        /** @type {DataGrid2} */
        this.dataGrid = dataGrid;
        /** @type {*[]} */
        this.rawData = this.dataGrid?.items || [];
        /** @type {number} */
        this.rawLength = this.rawData.length || 0;
        /** @type {*[]} */
        this.data = this.rawData.map((x) => x);
        /** @type {number} */
        this.length = this.rawLength;

        /** @type {ColumnDefinition[]} */
        this.columns = dataGrid?.renderedColumns || [];

        /** @type {integer[]|null} */
        this.proxyItems = null;

        /** @type {Function} */
        this.activeFilter = null;

        this.pendingRefresh = false;
        this.pendingActions = [];

        if(this.dataGrid) {
            this.dataGrid.filteredRows = this.data;
        }
    }

    postRefreshAction(action){
        if(!this.pendingRefresh){
            action();
            return;
        }

        this.pendingActions.push(action);
    }

    async closePendingRefresh(){
        return new Promise((resolve, reject) => {
            nextAnimationFrame(() => {
                this.pendingRefresh = false;
                let promises = [];
                this.pendingActions.forEach(a => {
                    try{
                        let r = a();
                        if(r && r.then && r.catch){
                            promises.push(r);
                        }
                    } catch (e){
                        Log.fatal(e);
                    }
                });
                this.pendingActions = [];
                if(promises.length){
                    Promise.all(promises).then(resolve).catch(reject);
                }
                resolve();
            });
        });
    }

    async refresh() {
        if(this.dataGrid) {


            this.columns = this.dataGrid.renderedColumns;
            this.rawData = this.dataGrid?.items || [];
            this.rawLength = this.rawData.length || 0;

            this.refreshFilters();
            if(!this.dataGrid.streaming){
                this.dataGrid.filteredRows = this.data;
                if(this.dataGrid.v3){

                    return;
                }
                this.pendingRefresh = true;
                this.dataGrid.gridComponent.refreshData().then( () => {
                    this.closePendingRefresh();
                    //this.dataGrid.gridComponent.refresh();
                    //this.refreshIndex();
                }).catch((e)=>{
                    this.closePendingRefresh();
                    throw e;
                });
            } else {
                this.dataGrid.filteredRows = [];
            }
        }
    }

    filter(predicate){
        filterSerial++;
        this.activeFilter = predicate;
        this.filterIsAsync = false;
        if(!predicate){
            this.data = this.rawData.map((x) => x);
            this.length = this.rawLength;
            this.dataGrid.filteredRows = this.data;
            return;
        }
        this.columns = this.dataGrid.renderedColumns;
        this.data = this.rawData.filter(predicate);
        this.length = this.data.length;

        this.dataGrid.filteredRows = this.data;
        //this.refreshIndex();
    }

    async asyncFilter(asyncPredicate){
        if(!asyncPredicate){
            this.clearFilter();
            return;
        }

        filterSerial++;
        const filterSnap = filterSerial;

        this.activeFilter = asyncPredicate;
        this.filterIsAsync = true;
        let allPromises = [];
        this.rawData.forEach(value => {
            const res = asyncPredicate(value);
            if(isPromise(res)){
                allPromises.push(res.then(resp => {
                    if(filterSnap === filterSerial){
                        value.__async_filter = !!resp;
                    }
                }).catch( e => {
                    console.log(e);
                    value.__async_filter = false;
                }));
            } else {
                value.__async_filter = !!res;
            }
        });
        await Promise.all(allPromises);
        if(filterSnap !== filterSerial){
            return;
        }

        this.columns = this.dataGrid.renderedColumns;
        this.data = this.rawData.filter(value => value.__async_filter);
        this.length = this.data.length;

        this.dataGrid.filteredRows = this.data;
        //this.refreshIndex();
    }

    clearFilter(){
        this.filter(null);
    }

    async filterByText(q){
        if(!q || q === ""){
            this.clearFilter();
            return;
        }

        await this.asyncFilter(row => {

            let allRowPromises = [];
            let promisesResults = false;

            for(let i in this.columns){
                let col = this.columns[i];
                if(col.searchable !== false && col.visible) {
                    let str = "";

                    if (col.toFilterStr !== null && col.toFilterStr !== undefined) {
                        str = col.toFilterStr(row[col.prop], row);
                        if (str) {
                            if(isPromise(str)){
                                allRowPromises.push(str.then(data => promisesResults = promisesResults || ("" + data).toLowerCase().includes(q)));
                            } else {
                                str = ("" + str).toLowerCase();
                                if (str.includes(q)) {
                                    return true;
                                }
                            }
                        }
                    } else if (col.toStrFromInstance !== null && col.toStrFromInstance !== undefined) {
                        str = col.toStrFromInstance(row);
                    } else if (col.toStr !== null && col.toStr !== undefined) {
                        str = col.toStr(row[col.prop]);
                    } else {
                        str = row[col.prop];
                    }

                    if(str) {
                        if(isPromise(str)){
                            allRowPromises.push(str.then(data => promisesResults = promisesResults || ("" + data).toLowerCase().includes(q)));
                        } else {
                            str = ("" + str).toLowerCase();
                            if (str.includes(q)) {
                                return true;
                            }
                        }
                    }
                }
            }

            if(allRowPromises.length){
                return Promise.all(allRowPromises).then(() => promisesResults);
            } else {
                return false;
            }
        });
    }

    refreshFilters(){
        if(this.activeFilter && this.filterIsAsync){
            this.asyncFilter(this.activeFilter);
        } else if(this.activeFilter){
            this.filter(this.activeFilter);
        } else {
            this.data = this.rawData.map((x) => x);
            this.length = this.rawLength;
        }
    }

    get(i){
        return this.data[i];
    }

    set(i, val){
        this.data[i] = val;
    }

    findIndex(predicate) {
        if(!this.dataGrid?.gridData){
            return;
        }
        const source = this.dataGrid.gridData.dataStore.get('source');
        return _.findIndex(source, predicate);
    }

    /**
     * @param {Function} predicate
     * @return {null|number}
     */
    indexOf(predicate) {
        return this.findIndex(predicate);
    }

    focusOnIndex(index, scroll=true){
        if(!this.dataGrid){
            return;
        }
        if(index === undefined || index == null ){
            if(this.dataGrid.gridComponent) {
                this.postRefreshAction(() => this.dataGrid.gridComponent.clearFocus());
            }
            return;
        }

        this.postRefreshAction(() => {
            if(scroll) {
                if(index < 0){
                    index = 0;
                }
                this.dataGrid.gridComponent?.scrollToRow(0).then(() => {

                    this.dataGrid.gridComponent?.scrollToRow(index).then(() =>
                        executeUntil(() => {
                            let div = this.dataGrid.gridData.querySelector(`div[data-row='${index}']`);
                            if (div) {
                                window.emulateClick(div.parentElement);
                                return true;
                            }
                            return false;
                        }, 5).then());
                });
            } else {
                executeUntil(() => {
                    let div = this.dataGrid.gridData.querySelector(`div[data-row='${index}']`);
                    if (div) {
                        window.emulateClick(div.parentElement);
                        return true;
                    }
                    return false;
                }, 5).then();

            }
        });
    }
    focusOn(predicate, scroll=true){
       this.focusOnIndex(this.indexOf(predicate) ?? 0, scroll);
    }

    focusOnRow(row, scroll=true){
        this.focusOnIndex(this.indexOf(r => JSON.stringify(r) == JSON.stringify(row)) ?? null, scroll);
    }
}